import { SxProps, Theme } from "@mui/material/styles";

const landscapeShortMedia =
  "@media (orientation: landscape) and (max-height: 560px)";

/** Shared page backdrop for Voice Messaging (both columns). */
export const sessionPageBg = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100];

/** Shared card fill/border — same on left and right. */
export const sessionCardStyles = (theme: Theme) => ({
  borderRadius: 2,
  padding: { xs: 1.25, sm: 1.5, md: 2 },
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
});

export const panelRootStyles: SxProps<Theme> = (theme) => ({
  flex: { xs: "1 1 0", sm: "1 1 0", md: "1 1 58%" },
  width: { xs: "100%", md: "auto" },
  minWidth: 0,
  minHeight: { xs: 220, sm: 240, md: 0 },
  display: "flex",
  flexDirection: "column",
  gap: 0,
  px: { xs: 1.25, sm: 2, md: 2 },
  py: { xs: 1, sm: 1.25, md: 2 },
  pb: {
    xs: "max(8px, env(safe-area-inset-bottom, 0px))",
    sm: "max(10px, env(safe-area-inset-bottom, 0px))",
    md: 2,
  },
  borderTop: {
    xs: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : theme.palette.divider
    }`,
    md: "none",
  },
  backgroundColor: sessionPageBg(theme),
  overflow: "hidden",
  [landscapeShortMedia]: {
    flex: "1 1 58%",
    width: "auto",
    minHeight: 0,
    borderTop: "none",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    px: 1.5,
    py: 0.75,
    gap: 0,
  },
});

// Objective / Focus / Status — three separate cards.
export const metaRootStyles: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1, sm: 1.25, md: 1.5 },
};

export const panelSectionStyles = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.25, sm: 0.5 },
  flexShrink: 0,
  ...sessionCardStyles(theme),
});

// Focus + Status: side-by-side on narrow left column / mobile.
export const panelMetaRowStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  justifyContent: "space-between",
  gap: { xs: 1, sm: 1.25 },
  flexShrink: 0,
};

export const panelMetaItemStyles = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.2,
  minWidth: 0,
  flex: "1 1 0",
  ...sessionCardStyles(theme),
});

export const panelTitleStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 10, sm: 11, md: 12 },
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
});

export const panelBodyStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 12, sm: 14, md: 15 },
  fontWeight: 500,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  whiteSpace: "pre-line",
});

export const panelBodyCompactStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 12, sm: 13, md: 14 },
  fontWeight: 500,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  whiteSpace: "pre-line",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: { xs: 2, sm: 3, md: 4 },
  overflow: "hidden",
});

export const panelStatusStyles = (color: string): SxProps<Theme> => ({
  fontSize: { xs: 13, sm: 15, md: 17 },
  fontWeight: 700,
  color,
});

/** Conversation + recorder docked in one highlighted card (both themes). */
export const conversationSectionStyles = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  flex: "1 1 0",
  minHeight: { xs: 200, sm: 220, md: 180 },
  overflow: "hidden",
  ...sessionCardStyles(theme),
  [landscapeShortMedia]: {
    minHeight: 140,
    flex: "1 1 auto",
  },
});

export const conversationBodyStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  flex: "1 1 0",
  minHeight: 0,
  overflow: "hidden",
};

export const conversationFooterStyles: SxProps<Theme> = {
  flexShrink: 0,
  pt: 0.5,
};

export const conversationListStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  flex: "1 1 0",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  pr: 0.5,
  pb: 1,
};
